/**
 * Mirrors backend BULLION odds resolution + line potential math (`bullionLinePotentialMath.js`,
 * `bullionSettlementFromDecimalApi.js`). Keeps board / played-view points aligned with settlement.
 */
import type { BullionMarket, BullionRunner, BullionSelection } from "./types";
import { BULLION_PTS_PER_CHIP, LEAGUE_BULLION_PTS_PER_CHIP } from "./constants";
import { bullionMarketStableId } from "./bullionCricketMarketGroups";
import { bullionOddsSnapshotMarkets } from "./bullionOddsSnapshot";
import { bullionSelectionStorageKey } from "./roomUtils";

const BULLION_TOSS_WINNER_DECIMAL_ODDS = 2;

function isBullionTossWinnerBoardMarket(market: Pick<BullionMarket, "marketTypeId" | "name">): boolean {
    const rawId = market.marketTypeId != null ? String(market.marketTypeId).trim() : "";
    const id = /^\d+(\.0+)?$/.test(rawId) ? String(parseInt(rawId, 10)) : rawId;
    const name = String(market.name ?? "").toLowerCase();
    return id === "815" || name.includes("toss winner");
}

function isBullionTossWinnerSnapshotMarket(sm: Record<string, unknown> | null): boolean {
    if (!sm) return false;
    const rawId = sm.market_type_id != null ? String(sm.market_type_id).trim() : "";
    const id = /^\d+(\.0+)?$/.test(rawId) ? String(parseInt(rawId, 10)) : rawId;
    const name = String(sm.market_name ?? sm.market_type ?? sm.name ?? "").toLowerCase();
    return id === "815" || name.includes("toss winner");
}

export function decimalIdsFromBullionMarketKey(marketIdOrKey: string): { baseMarketId: string; lineId: string } {
    const s = String(marketIdOrKey || "").trim();
    if (!s) return { baseMarketId: "", lineId: "" };
    if (/^\d+$/.test(s)) return { baseMarketId: s, lineId: "" };
    const twoNums = s.match(/^(\d+)_(\d+)$/);
    if (twoNums) {
        if (twoNums[1] === twoNums[2]) return { baseMarketId: twoNums[1]!, lineId: "" };
        return { baseMarketId: twoNums[1]!, lineId: twoNums[2]! };
    }
    const midDoubleUnderscore = s.match(/^(\d+)__/);
    if (midDoubleUnderscore) return { baseMarketId: midDoubleUnderscore[1]!, lineId: "" };
    return { baseMarketId: "", lineId: "" };
}

/** True when two slip/bonus market keys refer to the same board row. */
export function bullionMarketKeyEquivalence(a: unknown, b: unknown): boolean {
    const sa = String(a ?? "").trim();
    const sb = String(b ?? "").trim();
    if (!sa || !sb) return false;
    if (sa === sb) return true;
    const da = decimalIdsFromBullionMarketKey(sa);
    const db = decimalIdsFromBullionMarketKey(sb);
    const plainA = /^\d+$/.test(sa) ? sa : da.baseMarketId;
    const plainB = /^\d+$/.test(sb) ? sb : db.baseMarketId;
    if (plainA && plainB && plainA === plainB) {
        if (!da.lineId || !db.lineId || da.lineId === db.lineId) return true;
    }
    if (da.baseMarketId && db.baseMarketId && da.baseMarketId === db.baseMarketId) {
        if (!da.lineId || !db.lineId || da.lineId === db.lineId) return true;
    }
    return false;
}

function safePositiveNumber(v: unknown): number {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : 0;
}

function normalizeName(s: string): string {
    return String(s || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");
}

function findSnapshotMarket(oddsSnapshot: unknown, pickMarketId: string): Record<string, unknown> | null {
    const arr = bullionOddsSnapshotMarkets(oddsSnapshot);
    for (let i = 0; i < arr.length; i++) {
        const raw = arr[i];
        if (!raw || typeof raw !== "object") continue;
        const m = raw as Parameters<typeof bullionMarketStableId>[0];
        if (bullionMarketStableId(m, i) === String(pickMarketId || "")) {
            return raw as Record<string, unknown>;
        }
    }
    return null;
}

/**
 * Snapshot rows that share `market_id` but omit `market_line_id` on the pick: prefer an unambiguous
 * canonical line (`market_line_id === market_id`), else a single active+display row, else first in feed order.
 */
export function bullionDisambiguateDecimalMarketsWithoutLineId<
    T extends { market_id?: unknown; market_line_id?: unknown; active?: unknown; display?: unknown },
>(candidates: T[]): T | null {
    if (!candidates?.length) return null;
    if (candidates.length === 1) return candidates[0]!;
    const canonical = candidates.filter((m) => {
        const mid = String(m.market_id ?? "").trim();
        const lid = String(m.market_line_id ?? "").trim();
        return mid !== "" && lid === mid;
    });
    if (canonical.length === 1) return canonical[0]!;
    const isOn = (v: unknown) => v === 1 || v === true || String(v) === "1";
    const activeDisplayed = candidates.filter((m) => isOn(m.active) && isOn(m.display));
    if (activeDisplayed.length === 1) return activeDisplayed[0]!;
    return candidates[0]!;
}

function findSnapshotMarketByDecimalIds(
    oddsSnapshot: unknown,
    decimalMarketId: string,
    decimalMarketLineId: string | null
): Record<string, unknown> | null {
    const arr = bullionOddsSnapshotMarkets(oddsSnapshot);
    const mid = decimalMarketId != null ? String(decimalMarketId) : "";
    const lid = decimalMarketLineId != null ? String(decimalMarketLineId) : "";
    if (!mid) return null;
    const byMid = arr.filter((m) => m && typeof m === "object" && String((m as { market_id?: unknown }).market_id ?? "") === mid);
    if (!byMid.length) return null;
    if (lid) {
        const hit = byMid.find((m) => String((m as { market_line_id?: unknown }).market_line_id ?? "") === lid);
        return (hit ?? null) as Record<string, unknown> | null;
    }
    return bullionDisambiguateDecimalMarketsWithoutLineId(
        byMid as { market_id?: unknown; market_line_id?: unknown; active?: unknown; display?: unknown }[]
    ) as Record<string, unknown> | null;
}

export function oddsForBullionPick(snapshotMarket: Record<string, unknown> | null, pick: Record<string, unknown>): number {
    // Toss Winner is a 50/50: prefer the frozen snapshot price (which carries the admin's fixed 2.00 or a
    // margined price), falling back to the fixed 2.00 only when the snapshot has no usable toss odds.
    const isToss = isBullionTossWinnerSnapshotMarket(snapshotMarket);
    const sm = snapshotMarket;
    const selections = Array.isArray(sm?.selectionslist)
        ? sm.selectionslist
        : Array.isArray(sm?.selections)
          ? sm.selections
          : [];
    if (!selections.length) {
        if (isToss) return BULLION_TOSS_WINNER_DECIMAL_ODDS;
        return safePositiveNumber(pick?.odds ?? pick?.selectionOdds ?? pick?.recommended_price);
    }

    const byRunnerId = (selections as Record<string, unknown>[]).find(
        (s) => String(s.id ?? s.selection_id) === String(pick?.runnerId)
    );
    const byRunnerIdOdds = safePositiveNumber(byRunnerId?.odds ?? byRunnerId?.recommended_price);
    if (byRunnerIdOdds > 0) return byRunnerIdOdds;

    const wantName = normalizeName(String(pick?.selectionName ?? ""));
    if (wantName) {
        const byName = (selections as Record<string, unknown>[]).find(
            (s) => normalizeName(String(s?.name ?? "")) === wantName
        );
        const byNameOdds = safePositiveNumber(byName?.odds ?? byName?.recommended_price);
        if (byNameOdds > 0) return byNameOdds;
    }

    if (isToss) {
        const firstToss = safePositiveNumber(
            (selections as Record<string, unknown>[])[0]?.odds ??
                (selections as Record<string, unknown>[])[0]?.recommended_price
        );
        if (firstToss > 0) return firstToss;
        return BULLION_TOSS_WINNER_DECIMAL_ODDS;
    }

    return safePositiveNumber(pick?.odds ?? pick?.selectionOdds ?? pick?.recommended_price);
}

/**
 * When `liveOddsNum` is >0, that price wins (ephemeral board display). For persisted BULLION math,
 * use {@link bullionFrozenLineOddsForPlay} so stored lines do not follow the in-play ladder.
 */
export function bullionLineOddsForScoring(
    snapshotMarket: Record<string, unknown> | null,
    pick: Record<string, unknown>,
    liveOddsNum: number
): number {
    const live = Number(liveOddsNum);
    const fromLive = Number.isFinite(live) && live > 0 ? live : 0;
    const fromSnap = oddsForBullionPick(snapshotMarket, pick);
    let odds = fromLive > 0 ? fromLive : fromSnap > 0 ? fromSnap : 0;
    if (odds <= 0) {
        odds = Math.max(
            safePositiveNumber(pick?.odds),
            safePositiveNumber(pick?.selectionOdds),
            safePositiveNumber(pick?.recommended_price)
        );
    }
    return odds;
}

/**
 * Canonical `pick.odds` / `pick.lineOdds` from the play row (written on first snapshot resolve), else
 * `bullionLineOddsForScoring(..., 0)` — matches backend `bullionFrozenLineOddsForPlay`.
 */
export function bullionFrozenLineOddsForPlay(
    snapshotMarket: Record<string, unknown> | null,
    pick: Record<string, unknown>
): number {
    const fromPersist = Math.max(safePositiveNumber(pick.odds), safePositiveNumber(pick.lineOdds));
    if (fromPersist > 0) return fromPersist;
    return bullionLineOddsForScoring(snapshotMarket, pick, 0);
}

/** UI label for BULLION cells; feed from `bullionBoardLineOddsForDisplay` (snapshot first, then live row odds when snapshot misses). */
export function formatBullionBoardOddsLabel(oddsNum: number): string {
    if (Number.isFinite(oddsNum) && oddsNum > 0) return oddsNum.toFixed(2);
    return "—";
}

/**
 * Board price: opening `bullion_games.odds_snapshot` + persisted pick `odds`/`lineOdds` when set; if still unresolved, `runner.odds` from the feed.
 */
export function bullionBoardLineOddsForDisplay(
    oddsSnapshot: unknown,
    market: BullionMarket,
    runner: BullionRunner,
    rowSelection: BullionSelection | null | undefined
): number {
    const isToss = isBullionTossWinnerBoardMarket(market);
    const pickKey = bullionSelectionStorageKey(market);
    const sel: BullionSelection =
        rowSelection && rowSelection.runnerId === runner.id
            ? { ...rowSelection, marketId: pickKey }
            : {
                  marketId: pickKey,
                  runnerId: runner.id,
                  chips: 0,
                  groupIndex: market.groupIndex,
                  decimalMarketId: market.decimalMarketId ?? null,
                  decimalMarketLineId: market.decimalMarketLineId ?? null,
                  marketName: market.name ?? null,
                  selectionName: runner.name ?? null,
              };
    const pickRec = bullionPickRecordForScoring(sel);
    const snap = bullionFindSnapshotMarketForPickLoose(oddsSnapshot ?? null, pickRec, pickKey);
    const frozen = bullionFrozenLineOddsForPlay(snap, pickRec);
    if (frozen > 0) return frozen;
    // Toss Winner falls back to the fixed 50/50 price only when the snapshot has no frozen odds.
    if (isToss) return BULLION_TOSS_WINNER_DECIMAL_ODDS;
    // Snapshot join can miss some match lines (e.g. totals) while the live row still has Decimal odds.
    const fromRunner = Number(runner.odds);
    return Number.isFinite(fromRunner) && fromRunner > 0 ? fromRunner : 0;
}

export function bullionFindSnapshotMarketForPick(
    oddsSnapshot: unknown,
    pick: Record<string, unknown>,
    pickKey?: string
): Record<string, unknown> | null {
    const mk = pick.marketId ?? pick.market_id ?? pickKey;
    const marketIdKey = mk == null ? "" : String(mk).trim();
    if (!marketIdKey) return null;

    const fromKey = decimalIdsFromBullionMarketKey(marketIdKey);
    const numericPair =
        /^\d+_\d+$/.test(String(marketIdKey).trim()) && fromKey.baseMarketId && fromKey.lineId ? fromKey : null;
    const plainNumericMid = /^\d+$/.test(String(marketIdKey).trim());
    const dm = pick.decimalMarketId != null ? String(pick.decimalMarketId).trim() : "";
    const dl = pick.decimalMarketLineId != null ? String(pick.decimalMarketLineId).trim() : "";
    const snap =
        findSnapshotMarketByDecimalIds(oddsSnapshot, dm, dl || null) ||
        findSnapshotMarketByDecimalIds(oddsSnapshot, fromKey.baseMarketId, fromKey.lineId || null) ||
        (plainNumericMid ? findSnapshotMarketByDecimalIds(oddsSnapshot, String(marketIdKey).trim(), null) : null) ||
        findSnapshotMarket(oddsSnapshot, marketIdKey);
    return snap ?? null;
}

export function bullionFindSnapshotMarketForPickLoose(
    oddsSnapshot: unknown,
    pick: Record<string, unknown>,
    pickKey?: string
): Record<string, unknown> | null {
    const tight = bullionFindSnapshotMarketForPick(oddsSnapshot, pick, pickKey);
    if (tight) return tight;

    const arr = bullionOddsSnapshotMarkets(oddsSnapshot);
    const rid = pick?.runnerId != null ? String(pick.runnerId).trim() : "";
    if (!rid) return null;

    const marketsWithRunner: Record<string, unknown>[] = [];
    for (let i = 0; i < arr.length; i++) {
        const m = arr[i];
        if (!m || typeof m !== "object") continue;
        const rec = m as Record<string, unknown>;
        const sels = Array.isArray(rec.selectionslist)
            ? rec.selectionslist
            : Array.isArray(rec.selections)
              ? rec.selections
              : [];
        const has = (sels as Record<string, unknown>[]).some(
            (s) => String(s?.id ?? s?.selection_id ?? "").trim() === rid
        );
        if (has) marketsWithRunner.push(rec);
    }
    if (marketsWithRunner.length !== 1) {
        const wantMid = pick?.decimalMarketId != null ? String(pick.decimalMarketId).trim() : "";
        const wantLid = pick?.decimalMarketLineId != null ? String(pick.decimalMarketLineId).trim() : "";
        if (!wantMid) return null;
        const filtered = marketsWithRunner.filter((m) => String(m.market_id ?? "") === wantMid);
        if (wantLid) {
            const byLine = filtered.filter((m) => String(m.market_line_id ?? "") === wantLid);
            if (byLine.length === 1) return byLine[0]!;
            return null;
        }
        return bullionDisambiguateDecimalMarketsWithoutLineId(filtered);
    }
    return marketsWithRunner[0]!;
}

/**
 * Persisted/API picks may carry extra odds fields (`selectionOdds`, `recommended_price`, …).
 */
export function bullionPickRecordForScoring(sel: BullionSelectionLike): Record<string, unknown> {
    return {
        ...(sel as unknown as Record<string, unknown>),
        market_id: sel.marketId,
        marketId: sel.marketId,
    };
}

export type BullionSelectionLike = {
    marketId: string;
    runnerId: string;
    chips: number;
    groupIndex?: number;
    decimalMarketId?: string | null;
    decimalMarketLineId?: string | null;
    marketName?: string | null;
    selectionName?: string | null;
    settled?: boolean;
    settlementOutcome?: string | null;
    settlementResult?: string | null;
};

/** Chip units staked on a line: `chips × pts-per-chip`. Bonus points are staked separately. */
export function bullionLineChipUnits(
    chipsNorm: number,
    ptsPerChip: number = BULLION_PTS_PER_CHIP,
): number {
    const chips = Math.max(0, Math.round(Number(chipsNorm) || 0));
    const rate = Number.isFinite(ptsPerChip) && ptsPerChip > 0 ? ptsPerChip : BULLION_PTS_PER_CHIP;
    return chips * rate;
}

/**
 * Rounded line potential at decimal odds: `round(odds × (chip units + bonus))`.
 * `bonusPoints` (0 / 150 / 450) joins the stake, so a winning line pays it at the line's price.
 */
export function bullionLinePotentialFromOdds(
    odds: number,
    chipsNorm: number,
    bonusPoints = 0,
    ptsPerChip: number = BULLION_PTS_PER_CHIP,
): number {
    const o = Number(odds);
    const bonus = Math.max(0, Math.round(Number(bonusPoints) || 0));
    if (!Number.isFinite(o) || o <= 0) return bonus;
    return Math.round(o * (bullionLineChipUnits(chipsNorm, ptsPerChip) + bonus));
}

/** Pts-per-chip for the current Bullion surface (league battle vs cash). */
export function bullionPtsPerChipForLeagueMode(isLeagueMode: boolean): number {
    return isLeagueMode ? LEAGUE_BULLION_PTS_PER_CHIP : BULLION_PTS_PER_CHIP;
}
